<script lang="ts">
	//Services
	import { postReq, standardLogin } from '../../../services/http';
	import { format } from '../../../services/i18n';
	import sha1 from 'crypto-js/sha1';
	//Helpers
	import { passwordHasAllMandatorySymbols } from '../../../helpers/utilities';
	import { validateAllowedSymbols } from '../../../helpers/validators';
	//Design system components
	import { Button, Input, Row, Col, toast } from '@Shelly/shelly-design-system';
	import type { InputPropsTypes } from '@Shelly/shelly-design-system';
	// Types
	import PasswordUtility from 'src/components/PasswordUtility.svelte';
	import type { ChangePasswordRequest } from '../../../types/requests/settings/user/change_password';
	import { user_data } from 'src/stores/account/user';
	import { profiles } from 'src/stores/features/profiles';
	import { capFirstChar } from 'src/helpers/utilities';
	import { showForceLogoutAlert } from 'src/services/auth_errors';

	let currentPassValue = '',
		currentPassError: string = '',
		newPassError: string = '';

	let inputProps: {
		size: InputPropsTypes['size'];
		icon: InputPropsTypes['icon'];
		value: InputPropsTypes['value'];
		label: InputPropsTypes['label'];
		type: InputPropsTypes['type'];
		disabled: InputPropsTypes['disabled'];
		class: string;
		autocomplete: string;
	} = {
		icon: 'fas fa-key',
		size: 'm',
		class: '',
		disabled: false,
		value: '',
		label: 'new-password',
		type: 'password',
		autocomplete: 'new-password',
	};

	$: error = newPassError;

	const handleChangePassword = async (oldPass, newPass) => {
		if (validateAllowedSymbols(newPass)) {
			newPassError = $format('password_validate_allowed_symbols', {
				default:
					'Please use only letters (a – z and A – Z), numbers (0 – 9) and uppercase characters such as !@#$%^&*.()',
			});
			return;
		}

		const res: ChangePasswordRequest = await postReq('/user/change_password', {
			old_password: window.btoa(oldPass),
			new_password: window.btoa(newPass),
		});

		if (res.isok) {
			// Login migration: Relogin to obtain new tokens
			let res = await standardLogin($user_data.email, sha1(newPass).toString());
			if (!res.isok) {
				//Edge case: Relogin failed, but change password was successful
				// Current tokens are invalid, no other option than logout
				return await showForceLogoutAlert();
			}

			const activeProfile = $profiles.find((item) => item.isSelected);
			activeProfile.pass = sha1(newPass).toString();

			profiles.edit(activeProfile);
			toast.open({
				type: 'success',
				title: $format('setting_scc_saved_toast'),
			});
		} else {
			if (res?.errors?.wrong_old_password) {
				currentPassError = $format('toast_error_password_change_wrong_current_pass', {
					default: 'Wrong password.',
				});
			} else if (res.errors?.spwd) {
				newPassError = $format('toast_error_password_change_same_password', {
					default: 'Choose a password you haven’t used before.',
				});
			} else {
				toast.open({ type: 'danger', title: $format('setting_err_toast') });
			}
		}
	};

	const clearErrors = () => {
		if (currentPassError) {
			currentPassError = '';
		}
		if (newPassError) {
			newPassError = '';
		}
	};
</script>

<Row>
	<Col xs="12" md="3">
		<Input
			aria-labelledby="input-ph5v21d"
			icon={'fas fa-lock'}
			label={capFirstChar($format('placeholder_current_password'))}
			bind:value={currentPassValue}
			type={'password'}
			error={currentPassError}
			on:click={() => clearErrors()}
			id="input-ph5v21d"
		/>
	</Col>
	<Col xs="12" md="3">
		<PasswordUtility bind:inputProps bind:error on:click={() => clearErrors()} />
	</Col>
	<Col xs="12" md="3">
		<Button
			size="m"
			class="width-md-auto width-sm-full"
			disabled={!passwordHasAllMandatorySymbols(inputProps.value)}
			on:click={() => handleChangePassword(currentPassValue, inputProps.value)}
		>
			{$format('button_save')}
		</Button>
	</Col>
</Row>
